import type { Direction } from '../../shared/protocol.ts';

const KEY_DIRECTIONS: Readonly<Record<string, Direction>> = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
  KeyW: 'up', KeyS: 'down', KeyA: 'left', KeyD: 'right', // QWERTY
  KeyZ: 'up', KeyQ: 'left', // AZERTY
};

export const directionForKey = (code: string): Direction | undefined => KEY_DIRECTIONS[code];

/** Rayon (en part de la largeur de la croix) autour du centre où la direction ne change pas. */
const DPAD_DEAD_ZONE = 0.12;

/**
 * Directions demandées par le joueur. La dernière touche enfoncée est prioritaire ;
 * un appui très bref est mémorisé pour produire au moins un pas.
 */
export class DirectionInput {
  private held: Direction[] = [];
  private tapped: Direction | null = null;

  press(dir: Direction, isRepeat = false): void {
    this.held = [dir, ...this.held.filter((d) => d !== dir)];
    if (!isRepeat) this.tapped = dir;
  }

  release(dir: Direction): void {
    this.held = this.held.filter((d) => d !== dir);
  }

  clear(): void {
    this.held = [];
    this.tapped = null;
  }

  /** Direction à jouer pour le prochain pas, en consommant l'appui bref éventuel. */
  take(): Direction | null {
    const dir = this.held[0] ?? this.tapped;
    this.tapped = null;
    return dir;
  }

  /**
   * Branche la croix directionnelle tactile. Le doigt est suivi sur toute la croix (et au-delà) :
   * la direction vient de sa position par rapport au centre, si bien qu'on peut glisser d'une flèche
   * à l'autre sans relever le doigt. Près du centre, la dernière direction est gardée.
   */
  bindDpad(pad: HTMLElement): void {
    pad.hidden = false;
    const buttons = new Map([...pad.querySelectorAll<HTMLButtonElement>('[data-dir]')].map((button) => [button.dataset.dir as Direction, button]));
    let pointerId: number | null = null;
    let current: Direction | null = null;

    const set = (dir: Direction | null) => {
      if (dir === current) return;
      if (current) this.release(current);
      current = dir;
      if (dir) this.press(dir);
      for (const [key, button] of buttons) button.classList.toggle('held', key === dir);
    };
    const directionAt = (event: PointerEvent): Direction | null => {
      const rect = pad.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2), dy = event.clientY - (rect.top + rect.height / 2);
      // Zone morte au centre : on garde la direction en cours pour éviter les à-coups.
      if (Math.hypot(dx, dy) < rect.width * DPAD_DEAD_ZONE) return current;
      return Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
    };

    pad.addEventListener('pointerdown', (event) => {
      if (pointerId !== null) return;
      event.preventDefault();
      pointerId = event.pointerId;
      set(directionAt(event));
      // Capture : le doigt reste suivi même s'il sort de la croix (sans elle, le suivi s'arrête au bord).
      try {
        pad.setPointerCapture(event.pointerId);
      } catch { /* pointeur déjà relâché */ }
    });
    pad.addEventListener('pointermove', (event) => {
      if (event.pointerId === pointerId) set(directionAt(event));
    });
    const end = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      set(null);
    };
    pad.addEventListener('pointerup', end);
    pad.addEventListener('pointercancel', end);
    pad.addEventListener('lostpointercapture', end);
    pad.addEventListener('contextmenu', (event) => event.preventDefault());
  }
}
